<!--
  Super Admin: collections, billing cycles and Net Checkout order visibility
  (OPS-02, OPS-03, OPS-04).

  Plan: docs/planning/2026-09-02-stripe-payments-payouts-implementation-plan.md
  section "Unit 10 -- Ops and Super Admin surfaces".
  Requirements: OPS-02, OPS-03, OPS-04.

  RELATIONSHIP TO THE UNIT 7 PAGE, so nobody builds a third one.

  Unit 7 already ships /admin/distributor-invoices -- the REVIEW AND RELEASE
  surface, where a human approves each 1st/15th batch before it goes to
  Stripe (OPS-08). That page is about deciding to bill. This page is about
  what happened AFTERWARDS: whether the money arrived, and which cycles are
  outstanding. They are deliberately separate because the questions are
  asked at different times by people doing different jobs, and merging them
  would make the release gate one tab among five.

  ACH INITIATED IS NOT SETTLED CASH.

  The state column reads `is_settled` from the server, which reads Unit 8's
  single reader. Nothing in this component compares a state string. That
  discipline is the point: 50b section 19 #5 describes the exact bug where
  `state != 'failed_or_returned'` gets used as "paid" -- which is TRUE of
  `initiated` -- and Unit 8 answered it with a DB constraint, one reader and
  a source guard. This surface does not become the fourth place that gets it
  wrong.

  The consequence if it did: an operator sees an invoice as paid, releases
  the matching decorator settlement, and the debit comes back three days
  later. We would have paid out against money that never arrived.

  ASCII-only.
-->
<template>
    <div data-testid="sa-payments-collections">
        <h1 class="sa-page-title">Collections</h1>

        <p class="sa-pc-intro">
            ACH debits against distributor invoices, the billing cycles they
            belong to, and the Net Checkout orders behind them. An ACH debit
            takes several business days to settle and can be returned after
            it settles -- until it says Settled, the money has not arrived.
        </p>

        <p
            v-if="loading"
            class="sa-pc-state"
            data-testid="sa-payments-collections-loading"
        >
            Loading...
        </p>
        <p
            v-else-if="errorMsg"
            class="sa-pc-state sa-pc-state--error"
            data-testid="sa-payments-collections-error"
        >
            {{ errorMsg }}
        </p>

        <!--
          The settled marker for the ui-runner: present once every fetch has
          resolved, whether or not there is data. Without an anchor like
          this, a page whose requests never resolve would satisfy a
          "component mounted" assertion.
        -->
        <div v-else data-testid="sa-payments-collections-settled-state">
            <!-- OPS-04: open exceptions first. They are someone's queue. -->
            <section class="sa-pc-section">
                <h2 class="sa-pc-h2">Open exceptions</h2>
                <p
                    v-if="openExceptions.length === 0"
                    class="sa-pc-empty"
                    data-testid="sa-collections-exceptions-empty"
                >
                    No open collection exceptions.
                </p>
                <table
                    v-else
                    class="sa-table"
                    data-testid="sa-collections-exceptions-table"
                >
                    <thead>
                        <tr>
                            <th>Distributor</th>
                            <th>Kind</th>
                            <th>Detail</th>
                            <th>Payout already released?</th>
                            <th>Opened</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="e in openExceptions"
                            :key="e.id"
                            :data-testid="`sa-collections-exception-${e.id}`"
                        >
                            <td>{{ e.distributor_id }}</td>
                            <td>{{ e.kind }}</td>
                            <td>{{ e.detail || "--" }}</td>
                            <!--
                              Tri-state, rendered as three states. NULL is
                              "not determined", which is a different claim
                              from "no" -- and on this row, being wrong about
                              it is expensive.
                            -->
                            <td
                                :data-testid="
                                    `sa-collections-exception-released-${e.id}`
                                "
                            >
                                {{ releasedLabel(e.payout_already_released) }}
                            </td>
                            <td>{{ day(e.opened_at) }}</td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <!-- OPS-04: the attempts. -->
            <section class="sa-pc-section">
                <h2 class="sa-pc-h2">ACH collection attempts</h2>
                <p
                    v-if="attempts.length === 0"
                    class="sa-pc-empty"
                    data-testid="sa-collections-attempts-empty"
                >
                    No collection attempts yet. They appear here once a
                    distributor invoice is released to Stripe.
                </p>
                <table
                    v-else
                    class="sa-table"
                    data-testid="sa-collections-attempts-table"
                >
                    <thead>
                        <tr>
                            <th>Distributor</th>
                            <th>Invoice</th>
                            <th>Amount</th>
                            <th>State</th>
                            <th>Settled</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="a in attempts"
                            :key="a.id"
                            :data-testid="`sa-collections-attempt-${a.id}`"
                        >
                            <td>{{ a.distributor_id }}</td>
                            <td>{{ a.stripe_invoice_id || "--" }}</td>
                            <td>{{ money(a.amount_cents) }}</td>
                            <td
                                :data-testid="
                                    `sa-collections-attempt-state-${a.id}`
                                "
                            >
                                <span
                                    class="sa-pc-badge"
                                    :class="badgeClass(a)"
                                >{{ stateLabel(a) }}</span>
                                <div
                                    v-if="a.returned_from_settled"
                                    class="sa-pc-warn"
                                >
                                    Returned AFTER it had settled -- check
                                    whether a payout was already released.
                                </div>
                                <div
                                    v-if="a.failure_message"
                                    class="sa-pc-warn"
                                >
                                    {{ a.failure_message }}
                                </div>
                            </td>
                            <td>{{ day(a.settled_at) }}</td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <!-- OPS-03: the billing cycles. -->
            <section class="sa-pc-section">
                <h2 class="sa-pc-h2">Billing cycles</h2>
                <p
                    v-if="cycles.length === 0"
                    class="sa-pc-empty"
                    data-testid="sa-collections-cycles-empty"
                >
                    No billing cycles yet.
                </p>
                <table
                    v-else
                    class="sa-table"
                    data-testid="sa-collections-cycles-table"
                >
                    <thead>
                        <tr>
                            <th>Period</th>
                            <th>Invoices</th>
                            <th>Total</th>
                            <th>By status</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(c, i) in cycles"
                            :key="i"
                            :data-testid="`sa-collections-cycle-${i}`"
                        >
                            <td>
                                {{ day(c.period_start) }} to
                                {{ day(c.period_end) }}
                            </td>
                            <td>{{ c.invoice_count }}</td>
                            <td>{{ money(c.total_cents) }}</td>
                            <td>
                                <span
                                    v-for="(v, status) in c.by_status"
                                    :key="status"
                                    class="sa-pc-status-chip"
                                >
                                    {{ status }}: {{ v.count }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </section>

            <!-- OPS-02: the Net Checkout orders themselves. -->
            <section class="sa-pc-section">
                <h2 class="sa-pc-h2">Net Checkout orders</h2>
                <p
                    v-if="orders.length === 0"
                    class="sa-pc-empty"
                    data-testid="sa-collections-orders-empty"
                >
                    No Net Checkout orders yet.
                </p>
                <table
                    v-else
                    class="sa-table"
                    data-testid="sa-collections-orders-table"
                >
                    <thead>
                        <tr>
                            <th>Order</th>
                            <th>Store</th>
                            <th>Decoration owed</th>
                            <th>Shipped</th>
                            <th>Funding state</th>
                            <th>Authorized by</th>
                            <th>Invoice</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="o in orders"
                            :key="o.id"
                            :data-testid="`sa-collections-order-${o.id}`"
                        >
                            <td>
                                <router-link
                                    class="sa-pc-link"
                                    :to="
                                        `/admin/payments/reconciliation/${o.id}`
                                    "
                                >
                                    {{ o.order_key }}
                                </router-link>
                            </td>
                            <td>{{ o.store_name || "--" }}</td>
                            <td>{{ money(o.decoration_cents) }}</td>
                            <td>{{ o.shipped ? "Yes" : "No" }}</td>
                            <td>{{ o.funding_state || "--" }}</td>
                            <td
                                :data-testid="`sa-collections-order-authorized-${o.id}`"
                            >
                                {{ netAuthorizationLabel(o.net_authorization) }}
                            </td>
                            <!--
                              "Not billed yet" rather than a zero. An absent
                              invoice and a zero invoice are different claims.
                            -->
                            <td :data-testid="`sa-collections-order-invoice-${o.id}`">
                                <span v-if="o.invoice_id">
                                    #{{ o.invoice_id }}
                                </span>
                                <span v-else class="sa-pc-absent">
                                    Not billed yet
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </section>
        </div>
    </div>
</template>

<script>
import api from "@/services/api";
import { netAuthorizationLabel } from "@/utils/netAuthorization";

function money(cents) {
    if (cents === null || cents === undefined) return "--";
    return `$${(Number(cents) / 100).toFixed(2)}`;
}

function day(iso) {
    if (!iso) return "--";
    return String(iso).slice(0, 10);
}

export default {
    name: "SuperAdminPaymentsCollections",
    data() {
        return {
            loading: true,
            errorMsg: "",
            attempts: [],
            openExceptions: [],
            cycles: [],
            orders: [],
        };
    },
    async mounted() {
        await this.fetch();
    },
    methods: {
        netAuthorizationLabel,
        money,
        day,
        stateLabel(attempt) {
            // Reads the server's booleans, which read Unit 8's single
            // reader. No state string is compared in this component -- see
            // the file header on why that matters.
            if (attempt.is_settled) return "Settled";
            if (attempt.is_failed_or_returned) return "Failed or returned";
            return "In flight -- not settled";
        },
        badgeClass(attempt) {
            if (attempt.is_settled) return "sa-pc-badge--good";
            if (attempt.is_failed_or_returned) return "sa-pc-badge--bad";
            return "sa-pc-badge--pending";
        },
        releasedLabel(value) {
            if (value === true) return "Yes";
            if (value === false) return "No";
            // NULL. Saying "not determined" rather than "No" is the honest
            // rendering of a tri-state the model deliberately kept.
            return "Not determined";
        },
        async fetch() {
            this.loading = true;
            this.errorMsg = "";
            try {
                const [collections, cycles, orders] = await Promise.all([
                    api.get("/superadmin/payments/collections"),
                    api.get("/superadmin/payments/billing-cycles"),
                    api.get("/superadmin/payments/net-orders"),
                ]);
                const c = collections?.data || collections || {};
                this.attempts = c.attempts || [];
                this.openExceptions = c.open_exceptions || [];
                const cy = cycles?.data || cycles || {};
                this.cycles = cy.cycles || [];
                const o = orders?.data || orders || {};
                this.orders = o.orders || [];
            } catch (e) {
                this.errorMsg =
                    "Could not load collections. Please try again.";
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.sa-page-title {
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 8px;
    color: #1a202c;
}
.sa-pc-intro {
    font-size: 14px;
    color: #4a5568;
    margin: 0 0 16px;
    max-width: 760px;
}
.sa-pc-section {
    margin-bottom: 28px;
}
.sa-pc-h2 {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 8px;
    color: #2d3748;
}
.sa-pc-state {
    font-size: 14px;
    color: #4a5568;
    padding: 12px 0;
}
.sa-pc-state--error {
    color: #c53030;
}
.sa-pc-empty {
    font-size: 13px;
    color: var(--tf-fg-quiet);
    margin: 0;
}
.sa-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
}
.sa-table th,
.sa-table td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid #e2e8f0;
    vertical-align: top;
}
.sa-pc-badge {
    display: inline-block;
    font-size: 12px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
}
.sa-pc-badge--good {
    background: #f0fff4;
    color: #2f855a;
}
.sa-pc-badge--pending {
    background: #fffaf0;
    color: #b7791f;
}
.sa-pc-badge--bad {
    background: #fff5f5;
    color: #c53030;
}
.sa-pc-warn {
    font-size: 12px;
    color: #c05621;
    margin-top: 2px;
}
.sa-pc-absent {
    color: var(--tf-fg-quiet);
    font-style: italic;
    font-size: 13px;
}
.sa-pc-status-chip {
    display: inline-block;
    font-size: 12px;
    color: #4a5568;
    background: #edf2f7;
    border-radius: 8px;
    padding: 1px 6px;
    margin-right: 4px;
}
.sa-pc-link {
    color: #2b6cb0;
    text-decoration: none;
}
</style>
